import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import moment from 'moment';
import 'moment/locale/th';
import { SERVICE_URL } from '../../utils/api';
import shrinkImage from './shrinkImage';

// หน้าลงเวลาเข้า-ออกงาน (ลิงก์ส่วนตัวของแม่บ้าน /once/cleaner/clock?t=) -- docs/cleaner-attendance-plan.md (API)
// ขั้นตอน: ถ่ายเซลฟี่ (ถ้าที่พักบังคับ) -> กดปุ่ม -> ขอ GPS -> ส่ง

const getLocation = () => new Promise((resolve, reject) => {
  if (!navigator.geolocation) return reject(new Error('เครื่องนี้ไม่รองรับ GPS'));
  navigator.geolocation.getCurrentPosition(
    (p) => resolve(p.coords),
    (e) => reject(new Error(e.code === 1
      ? 'กรุณาอนุญาตให้ LINE / เบราว์เซอร์ เข้าถึงตำแหน่ง (Location) แล้วลองใหม่'
      : 'หาตำแหน่งไม่ได้ ลองออกไปที่โล่งแล้วกดใหม่')),
    { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 },
  );
});

const errMsg = (e) => e?.response?.data?.message || e?.message || 'เกิดข้อผิดพลาด ลองใหม่อีกครั้ง';

export default function CleanerClock() {
  const token = new URLSearchParams(window.location.search).get('t');
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const [now, setNow] = useState(new Date());
  const [photoUrl, setPhotoUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null); // { ok, text }
  const fileRef = useRef(null);

  const cw = useCallback((path, opts = {}) => {
    const sep = path.includes('?') ? '&' : '?';
    return axios({ baseURL: SERVICE_URL, url: `cleaner-web/${path}${sep}t=${encodeURIComponent(token || '')}`, ...opts });
  }, [token]);

  const load = useCallback(() => {
    if (!token) { setError('ลิงก์ไม่ถูกต้อง กรุณาขอลิงก์ใหม่จากแอดมิน'); return; }
    cw('attendance').then((r) => setStatus(r.data)).catch((e) => setError(errMsg(e)));
  }, [cw, token]);

  useEffect(() => { moment.locale('th'); load(); }, [load]);
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t); }, []);

  const onPhoto = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true); setMsg(null);
    try {
      const small = await shrinkImage(file, 1024, 0.75);
      const fd = new FormData();
      fd.append('file', small, 'selfie.jpg');
      const r = await cw('upload-photo', { method: 'post', data: fd, timeout: 90000 });
      setPhotoUrl(r.data.url);
    } catch (err) {
      setMsg({ ok: false, text: 'ส่งรูปไม่สำเร็จ สัญญาณเน็ตอาจไม่ดี ลองถ่ายใหม่' });
    }
    setUploading(false);
  };

  const submit = async () => {
    if (!status) return;
    if (status.require_photo && !photoUrl) { setMsg({ ok: false, text: 'กรุณาถ่ายรูปก่อน' }); return; }
    const type = status.clocked_in ? 'out' : 'in';
    if (type === 'out' && !window.confirm('ยืนยันเช็คเอาท์ (เลิกงาน)?')) return;
    setBusy(true); setMsg(null);
    try {
      const c = await getLocation();
      const r = await cw('attendance/clock', {
        method: 'post',
        data: { type, lat: c.latitude, lng: c.longitude, accuracy: c.accuracy, photo_url: photoUrl },
      });
      setStatus((s) => ({ ...s, ...r.data.status }));
      setPhotoUrl(null);
      setMsg({ ok: true, text: `${type === 'in' ? 'เช็คอิน' : 'เช็คเอาท์'}เรียบร้อย ${moment(r.data.log.at).format('HH:mm')} น.` });
    } catch (e) {
      setMsg({ ok: false, text: errMsg(e) });
    }
    setBusy(false);
  };

  // ---------- styles (โทนเดียวกับ CleanerWork) ----------
  const S = {
    page: { boxSizing: 'border-box', width: '100%', maxWidth: 480, margin: '0 auto', paddingTop: 76, paddingRight: 16, paddingBottom: 48, paddingLeft: 16, color: '#1A211D', minHeight: '100vh' },
    h: { fontSize: 20, fontWeight: 800, margin: 0 },
    sub: { fontSize: 13, color: '#586159' },
    card: { boxSizing: 'border-box', border: '1.5px solid #DBE2DC', borderRadius: 14, padding: 16, marginBottom: 12, background: '#fff' },
    bigBtn: (bg) => ({ boxSizing: 'border-box', width: '100%', border: 'none', borderRadius: 16, padding: 20, fontSize: 22, fontWeight: 800, color: '#fff', background: bg, opacity: busy ? 0.6 : 1 }),
    ghost: { boxSizing: 'border-box', width: '100%', border: '1.5px dashed #CBD4CC', borderRadius: 14, padding: 14, fontSize: 16, fontWeight: 700, background: '#fff', color: '#1A211D' },
    msg: (ok) => ({ borderRadius: 12, padding: '12px 14px', marginBottom: 12, fontWeight: 700, color: ok ? '#0F5D3B' : '#A12C23', background: ok ? '#E7F4EC' : '#FBEAE8' }),
  };

  if (error) {
    return <div style={{ ...S.page, textAlign: 'center', paddingTop: 80 }}>
      <div style={{ fontSize: 40 }}>🔒</div>
      <p style={{ marginTop: 12, color: '#586159' }}>{error}</p>
    </div>;
  }
  if (!status) return <div style={{ ...S.page, textAlign: 'center', paddingTop: 80, color: '#586159' }}>กำลังโหลด…</div>;

  if (!status.enabled) {
    return <div style={{ ...S.page, textAlign: 'center', paddingTop: 80 }}>
      <div style={{ fontSize: 40 }}>🕒</div>
      <p style={{ marginTop: 12, color: '#586159' }}>ที่พักนี้ยังไม่เปิดระบบลงเวลา กรุณาแจ้งแอดมิน</p>
    </div>;
  }

  const inColor = '#0F8A56', outColor = '#C2410C';
  const todayLogs = (status.recent || []).filter((l) => moment(l.at).isSame(moment(), 'day'));

  return (
    <div style={S.page}>
      <div style={{ marginBottom: 12 }}>
        <h1 style={S.h}>สวัสดีค่ะ พี่{status.cleaner.firstname} 🕒</h1>
        <div style={S.sub}>ลงเวลาเข้า-ออกงาน · {status.hotel?.name || ''}</div>
      </div>

      {msg && <div style={S.msg(msg.ok)}>{msg.text}</div>}

      <div style={{ ...S.card, textAlign: 'center' }}>
        <div style={{ fontSize: 48, fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>{moment(now).format('HH:mm:ss')}</div>
        <div style={S.sub}>{moment(now).format('dddd D MMMM YYYY')}</div>
        <div style={{ marginTop: 10, fontWeight: 700, color: status.clocked_in ? inColor : '#8A938B' }}>
          {status.clocked_in ? `● กำลังทำงาน (เข้า ${moment(status.since).format(moment(status.since).isSame(moment(), 'day') ? 'HH:mm' : 'D MMM HH:mm')} น.)` : '○ ยังไม่ได้เช็คอิน'}
        </div>
      </div>

      {status.require_photo && (
        <div style={S.card}>
          <input ref={fileRef} type="file" accept="image/*" capture="user" onChange={onPhoto} style={{ display: 'none' }} />
          <button type="button" style={S.ghost} disabled={uploading || busy} onClick={() => fileRef.current && fileRef.current.click()}>
            {uploading ? 'กำลังส่งรูป…' : photoUrl ? '📷 ถ่ายใหม่' : '📷 ถ่ายเซลฟี่ยืนยันตัวตน'}
          </button>
          {photoUrl && <img src={photoUrl} alt="รูปที่ถ่าย" style={{ width: '100%', borderRadius: 12, marginTop: 10 }} />}
        </div>
      )}

      <button type="button" style={S.bigBtn(status.clocked_in ? outColor : inColor)} disabled={busy || uploading} onClick={submit}>
        {busy ? 'กำลังบันทึก…' : status.clocked_in ? 'เช็คเอาท์ (เลิกงาน)' : 'เช็คอิน (เข้างาน)'}
      </button>
      <div style={{ ...S.sub, textAlign: 'center', marginTop: 8 }}>ต้องอยู่ในพื้นที่ที่พัก (รัศมี {status.radius_m} ม.) และเปิด GPS</div>

      {todayLogs.length > 0 && (
        <div style={{ ...S.card, marginTop: 16 }}>
          <div style={{ fontWeight: 800, marginBottom: 6 }}>วันนี้</div>
          {todayLogs.map((l) => (
            <div key={l.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderTop: '1px solid #EEF1EE' }}>
              <span style={{ color: l.type === 'in' ? inColor : outColor, fontWeight: 700 }}>{l.type === 'in' ? 'เข้า' : 'ออก'}{l.source === 'admin' ? ' (แอดมินบันทึก)' : ''}</span>
              <span style={{ fontVariantNumeric: 'tabular-nums' }}>{moment(l.at).format('HH:mm')} น.</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
